import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import {
  ArrowRight,
  MapPinned,
  Ruler,
  Pencil,
  Route,
  LocateFixed,
  FileText,
  Cloud,
  CheckCircle2,
  Download,
  Trees,
  Zap
} from "lucide-react";
import { BRAND, STATS, FEATURES, TESTIMONIALS } from "../data/mock";

const iconMap = { Ruler, Pencil, Route, LocateFixed, FileText, Cloud };

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200">
        <div className="hero-grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <Badge className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 border-emerald-200 mb-5">
              <Trees className="h-3 w-3 mr-1.5" /> Built for field professionals
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 leading-[1.05] tracking-tight">
              Offline maps that move with your crew.
            </h1>
            <p className="mt-5 text-lg text-stone-600 max-w-xl leading-relaxed">
              {BRAND.name} turns georeferenced PDFs into living field tools — with GPS, measurement, shapefile and GPX export for forestry, transmission line and environmental work.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/dashboard">
                <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 h-12 px-6">
                  <MapPinned className="h-4 w-4" /> Open My Maps
                </Button>
              </Link>
              <Link to="/features">
                <Button size="lg" variant="outline" className="border-stone-300 text-stone-800 hover:bg-stone-50 h-12 px-6 gap-2">
                  See Features <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-600">
              {["Works offline", "GPX & Shapefile export", "Real GPS positioning"].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Hero illustration card */}
          <div className="fade-up">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100 to-stone-100 rounded-3xl blur-2xl opacity-70" />
              <Card className="relative overflow-hidden rounded-2xl border-stone-200 shadow-xl">
                <div className="relative aspect-[5/4]">
                  <img
                    src="https://images.unsplash.com/photo-1448375240586-882707db888b?w=1200&q=80&auto=format&fit=crop"
                    alt="Forestry field map"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
                  {/* Floating UI bits */}
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-md flex items-center gap-2">
                    <div className="gps-marker !w-3 !h-3 !border-2" />
                    <span className="text-xs font-medium text-stone-700">GPS Locked • ±3.1 m</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                    <div className="bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-md flex items-center gap-2 text-xs font-medium text-stone-700">
                      <Ruler className="h-3.5 w-3.5 text-emerald-700" /> 1.42 km
                    </div>
                    <div className="bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-md flex items-center gap-2 text-xs font-medium text-stone-700">
                      <Pencil className="h-3.5 w-3.5 text-emerald-700" /> Polygon • 18 vertices
                    </div>
                    <div className="hidden sm:flex bg-emerald-700 text-white rounded-lg px-3 py-2 shadow-md items-center gap-2 text-xs font-semibold">
                      <Download className="h-3.5 w-3.5" /> Export .shp
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.id} className="text-center md:text-left">
              <div className="text-3xl lg:text-4xl font-extrabold text-stone-900">{s.value}</div>
              <div className="text-sm text-stone-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="max-w-2xl">
          <Badge className="bg-stone-100 text-stone-700 hover:bg-stone-100 border-stone-200 mb-4">
            <Zap className="h-3 w-3 mr-1.5" /> Everything you need in the field
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-stone-900">Powerful tools, ready offline.</h2>
          <p className="mt-3 text-stone-600 text-lg">From a single tap on a georeferenced PDF to a full GIS-ready dataset back at the office.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => {
            const Icon = iconMap[f.icon] || MapPinned;
            return (
              <Card key={f.id} className="group p-6 border-stone-200 hover:border-emerald-300 hover:shadow-md transition-all duration-200 bg-white">
                <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-stone-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{f.description}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="border-y border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold text-stone-900">Your maps. Your crew. One tap away.</h2>
            <p className="mt-3 text-stone-600 text-lg max-w-xl">Browse your uploaded and downloaded georeferenced maps from the dashboard. Open any map to measure, draw and export in seconds.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/dashboard">
              <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2 h-12 px-6">
                <MapPinned className="h-4 w-4" /> Open My Maps
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-stone-300 text-stone-800 hover:bg-white h-12 px-6">View Pricing</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <h2 className="text-3xl lg:text-4xl font-bold text-stone-900 max-w-2xl">Trusted by crews from coast to coast.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <Card key={t.id} className="p-6 border-stone-200 bg-white">
              <div className="text-emerald-700 text-3xl leading-none">“</div>
              <p className="text-stone-700 leading-relaxed text-[15px]">{t.quote}</p>
              <div className="mt-5 pt-5 border-t border-stone-200">
                <div className="font-semibold text-stone-900 text-sm">{t.name}</div>
                <div className="text-xs text-stone-500">{t.role}</div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
